'use client';
import React from 'react';
import PropTypes from 'prop-types';

import Checkbox from '../Checkbox/Checkbox';

import s from './MapLayerList.module.scss';

/**
 * Switch a map's layers on and off, one checkbox per layer.
 *
 * Driven entirely by the layer array it is handed, so whatever else draws those
 * layers — the map itself — reads the same list rather than keeping a parallel
 * description of it. The world map's flowmap picker is hardcoded to its
 * one checkbox; this is the array-driven one it could move onto.
 *
 * Brings no chrome of its own: which layers are drawn is one filter among
 * however many a map offers, so it sits inside a `Dropdown` section of
 * `MapFilterPanel` beside the rest, drawn as the holdings filter draws its
 * checkbox rows.
 */
const MapLayerList = ({ layers = [], onToggle, title, renderSwatch }) => {
    // With nothing to pick between, a list is chrome over an answer nobody
    // asked for.
    if (!layers.length) {
        return null;
    }

    return (
        <ul className={s.Options} aria-label={title}>
            {layers.map((layer) => (
                <li className={s.Option} key={layer.id}>
                    {/* The checkbox already announces the layer's name, and
                        announcing it twice per row would make the list twice
                        as long to listen to. */}
                    <span className={s.Label} aria-hidden="true">
                        {renderSwatch && renderSwatch(layer)}
                        {layer.name}
                    </span>
                    <Checkbox
                        modifier="Alternative"
                        id={layer.id}
                        label={layer.name}
                        checked={layer.isVisible}
                        onChange={() => onToggle(layer.id, !layer.isVisible)}
                    />
                </li>
            ))}
        </ul>
    );
};

MapLayerList.propTypes = {
    layers: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.string.isRequired,
            name: PropTypes.string,
            isVisible: PropTypes.bool,
        })
    ),
    // Called with the layer's id and the visibility it should take.
    onToggle: PropTypes.func.isRequired,
    // The list's accessible name. The section header around it carries the
    // visible one.
    title: PropTypes.string.isRequired,
    // Whatever marks a layer out on the map — an icon, a colour, a line style.
    // The list only places it; what it looks like is the caller's business.
    renderSwatch: PropTypes.func,
};

export default MapLayerList;
